import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Search, PlayCircle, AlertTriangle, Activity, MapPin, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { StatusBadge } from '@/components/StatusBadge';
import { VinScanner } from '@/components/VinScanner';
import { useAuth } from '@/contexts/AuthContext';
import { SiteSelector } from '@/components/SiteSelector';
import { formatDistanceToNow } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';
import { logStatusChange, logCarEvent } from '@/services/carHistoryService';
import AppHeader from '@/components/AppHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Car {
  id: string;
  vin: string;
  status: string;
  model: string | null;
  color: string | null;
  started_at: string | null;
  completed_at: string | null;
  current_operator_id: string | null;
  claimed_at: string | null;
  last_activity_at: string | null;
  current_operator?: {
    full_name: string;
  } | null;
}

interface StationClaim {
  id: string;
  station_id: string;
  station: {
    line_id?: string;
    station_number: number;
    name: string | null;
  };
}

const Operator = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('cs') ? cs : enUS;
  const [vin, setVin] = useState('');
  const [loading, setLoading] = useState(false);
  const [cars, setCars] = useState<Car[]>([]);
  const [loadingCars, setLoadingCars] = useState(true);
  const [forceClaimCar, setForceClaimCar] = useState<Car | null>(null);
  const [stationClaim, setStationClaim] = useState<StationClaim | null>(null);
  const [loadingStation, setLoadingStation] = useState(true);
  const navigate = useNavigate();
  const { user, currentLineId, setCurrentLineId, currentTenantId } = useAuth();

  useEffect(() => {
    setLoadingCars(true);
    setLoadingStation(true);
    setCars([]);
    setStationClaim(null);
    fetchCars(currentLineId);
    loadStationClaim(currentLineId);
  }, [currentLineId, user, currentTenantId]);

  const loadStationClaim = async (lineId?: string | null) => {
    if (!user || !currentTenantId) {
      setLoadingStation(false);
      return;
    }

    try {
      // First, get all active station claims for the user
      const { data: claims, error: claimsError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('station_claims')
        .select(`
          id,
          station_id,
          station:stations(station_number, name, line_id)
        `)
        .eq('user_id', user.id)
        .eq('tenant_id', currentTenantId)
        .is('released_at', null);

      if (claimsError) throw claimsError;

      // Filter by line_id on the client side if specified
      let filteredClaim = null;
      if (claims && claims.length > 0) {
        if (lineId) {
          // Find claim that matches the current line
          filteredClaim = claims.find(claim =>
            claim.station && (claim.station as any).line_id === lineId
          ) || null;
        } else {
          // No site filter, use first claim
          filteredClaim = claims[0];
        }
      }

      setStationClaim(filteredClaim as StationClaim | null);
    } catch (error: any) {
      console.error('Error loading station claim:', error);
      setStationClaim(null);
    } finally {
      setLoadingStation(false);
    }
  };

  const fetchCars = async (lineId?: string | null) => {
    if (!currentTenantId) {
      setCars([]);
      setLoadingCars(false);
      return;
    }
    try {
      let query = ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .select('*')
        .order('created_at', { ascending: false });

      if (currentTenantId) {
        query = query.eq('tenant_id', currentTenantId);
      }

      if (lineId) {
        query = query.eq('line_id', lineId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const carsData = (data || []) as Car[];
      const operatorIds = Array.from(new Set(
        carsData.map(car => car.current_operator_id).filter(Boolean)
      )) as string[];

      if (operatorIds.length > 0 && currentTenantId) {
        const { data: profiles } = await ((supabase as any)
          .schema('mod_styling'))
          .from('v_profiles')
          .select('id, full_name')
          .in('id', operatorIds)
          .eq('tenant_id', currentTenantId);

        const profileMap = new Map<string, string | null>(
          (profiles || []).map((profile: any) => [profile.id, profile.full_name || null])
        );

        setCars(carsData.map(car => ({
          ...car,
          current_operator: car.current_operator_id
            ? { full_name: profileMap.get(car.current_operator_id) ?? '' }
            : undefined,
        })));
      } else {
        setCars(carsData);
      }
    } catch (error: any) {
      toast.error(t('operator.failedToLoad'));
    } finally {
      setLoadingCars(false);
    }
  };

  const claimCar = async (carId: string, force = false) => {
    if (!user || !currentTenantId) {
      toast.error(t('operator.loginToClaim'));
      return;
    }

    console.log('[Operator] Starting car claim process:', {
      carId,
      force,
      userId: user.id,
      currentLineId,
      stationClaim: stationClaim?.id
    });

    try {
      // First, release any car currently claimed by this user
      const currentlyClaimedCars = cars.filter(c => c.current_operator_id === user.id && c.id !== carId);

      if (currentlyClaimedCars.length > 0) {
        const { error: releaseError } = await ((supabase as any)
          .schema('mod_styling'))
          .from('cars')
          .update({
            current_operator_id: null,
            last_activity_at: new Date().toISOString(),
          })
          .eq('tenant_id', currentTenantId)
          .in('id', currentlyClaimedCars.map(c => c.id));

        if (releaseError) {
          console.error('Failed to release previous cars:', releaseError);
          // Continue anyway - don't block the new claim
        } else {
          // Log release events for each car
          if (currentLineId && currentTenantId) {
            for (const releasedCar of currentlyClaimedCars) {
              await logCarEvent({
                carId: releasedCar.id,
                eventType: 'car_released',
                triggeredBy: user.id,
                lineId: currentLineId,
                tenantId: currentTenantId,
                data: { reason: 'claimed_another_car', new_car_id: carId },
              });
            }
          }
          
          if (currentlyClaimedCars.length === 1) {
            toast.info(t('operator.releasedSingle', { vin: currentlyClaimedCars[0].vin }));
          } else {
            toast.info(t('operator.releasedMultiple', { count: currentlyClaimedCars.length }));
          }
        }
      }

      // Get current car data for logging
      const carData = cars.find(c => c.id === carId);
      const previousOperatorId = carData?.current_operator_id;

      // Now claim the new car
      console.log('[Operator] Updating car in database:', carId);
      const { data: updatedCar, error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .update({
          current_operator_id: user.id,
          claimed_at: new Date().toISOString(),
          last_activity_at: new Date().toISOString(),
          status: 'in_progress',
          started_at: new Date().toISOString(),
        })
        .eq('id', carId)
        .eq('tenant_id', currentTenantId)
        .select()
        .single();

      if (error) throw error;

      console.log('[Operator] Car claimed successfully:', {
        carId,
        updatedCar,
        current_operator_id: updatedCar?.current_operator_id,
        userId: user.id,
        match: updatedCar?.current_operator_id === user.id
      });

      // Log status change and event
      if (currentLineId && currentTenantId) {
        await logStatusChange({
          carId,
          status: 'in_progress',
          changedBy: user.id,
          reason: force ? 'car_taken_over' : 'car_claimed',
          tenantId: currentTenantId,
          metadata: {
            station_id: stationClaim?.station_id,
            previous_operator_id: previousOperatorId,
          },
        });

        await logCarEvent({
          carId,
          eventType: force ? 'car_taken_over' : 'car_claimed',
          triggeredBy: user.id,
          lineId: currentLineId,
          tenantId: currentTenantId,
          data: {
            station_id: stationClaim?.station_id,
            previous_operator_id: previousOperatorId,
          },
        });
      }

      // Update station_claims to set current_car_id
      const { error: stationError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('station_claims')
        .update({ current_car_id: carId })
        .eq('user_id', user.id)
        .eq('tenant_id', currentTenantId)
        .is('released_at', null);

      if (stationError) {
        console.error('Failed to update station claim with car:', stationError);
        // Don't block - car claim succeeded
      }

      toast.success(force ? t('operator.carTakenOver') : t('operator.carClaimed'));

      console.log('[Operator] Navigating to work page:', `/work/${carId}`);
      navigate(`/work/${carId}`);
    } catch (error: any) {
      console.error('[Operator] Failed to claim car:', error);
      toast.error(error.message ? `${t('operator.failedToClaim')}: ${error.message}` : t('operator.failedToClaim'));
    }
  };

  const handleClaimCar = async (car: Car) => {
    if (!user) {
      toast.error(t('operator.loginToClaim'));
      return;
    }

    // Check if user has claimed a station
    if (!stationClaim) {
      toast.error(t('operator.mustClaimStation'));
      return;
    }

    // Check if car is claimed by someone else
    if (car.current_operator_id && car.current_operator_id !== user.id) {
      setForceClaimCar(car);
      return;
    }

    // If already claimed by current user, just navigate
    if (car.current_operator_id === user.id) {
      navigate(`/work/${car.id}`);
      return;
    }

    // Claim available car
    await claimCar(car.id);
  };

  const handleForceClaim = async () => {
    if (!forceClaimCar) return;
    await claimCar(forceClaimCar.id, true);
    setForceClaimCar(null);
  };

  const handleVinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vin.trim()) {
      toast.error(t('operator.enterVin'));
      return;
    }

    if (!currentLineId || !currentTenantId) {
      toast.error(t('operator.noSiteSelected'));
      return;
    }

    setLoading(true);

    try {
      // Check if car exists
      const { data: existingCar, error: fetchError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .select('*')
        .eq('vin', vin.toUpperCase())
        .eq('tenant_id', currentTenantId)
        .eq('line_id', currentLineId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existingCar) {
        let enrichedCar = existingCar as Car;

        if (existingCar.current_operator_id && currentTenantId) {
          const { data: profile } = await ((supabase as any)
            .schema('mod_styling'))
            .from('v_profiles')
            .select('full_name')
            .eq('id', existingCar.current_operator_id)
            .eq('tenant_id', currentTenantId)
            .maybeSingle();

          enrichedCar = {
            ...existingCar,
            current_operator: { full_name: profile?.full_name || '' },
          } as Car;
        }

        await handleClaimCar(enrichedCar);
      } else {
        toast.error(t('operator.vehicleNotFound'));
      }
    } catch (error: any) {
      toast.error(error.message ? `${t('operator.failedToProcessVin')}: ${error.message}` : t('operator.failedToProcessVin'));
    } finally {
      setLoading(false);
    }
  };

  const handleScanSuccess = (scannedVin: string) => {
    setVin(scannedVin);
    setTimeout(() => {
      const form = document.getElementById('vin-form') as HTMLFormElement;
      if (form) {
        form.requestSubmit();
      }
    }, 500);
  };

  const formatRelativeTime = (date: string | null) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: dateLocale });
    } catch {
      return '';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getButtonLabel = (car: Car) => {
    if (!user) return t('operator.buttons.start');
    if (car.current_operator_id === user.id) return t('operator.buttons.continue');
    if (car.current_operator_id) return t('operator.buttons.takeOver');
    if (car.status === 'completed') return t('operator.buttons.restart');
    return t('operator.buttons.claim');
  };

  const getButtonVariant = (car: Car): 'default' | 'outline' | 'destructive' => {
    if (!user) return 'outline';
    if (car.current_operator_id && car.current_operator_id !== user.id) return 'destructive';
    return 'outline';
  };

  // Filter cars based on VIN input
  const filteredCars = cars.filter(car =>
    car.vin.toUpperCase().includes(vin.toUpperCase())
  );

  // Separate cars into categories
  const availableCars = filteredCars.filter(car => !car.current_operator_id);
  const claimedCars = filteredCars.filter(car => car.current_operator_id);
  const myCars = claimedCars.filter(car => car.current_operator_id === user?.id);
  const othersCars = claimedCars.filter(car => car.current_operator_id !== user?.id);

  const renderCarTable = (cars: Car[], emptyMessage: string) => {
    if (cars.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          {emptyMessage}
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('carsManagement.tableHeaders.vin')}</TableHead>
            <TableHead>{t('carsManagement.tableHeaders.model')}</TableHead>
            <TableHead>{t('carsManagement.tableHeaders.status')}</TableHead>
            <TableHead>{t('carsManagement.tableHeaders.currentOperator')}</TableHead>
            <TableHead>{t('operator.tableHeaders.activity')}</TableHead>
            <TableHead className="text-right">{t('operator.tableHeaders.action')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cars.map((car) => (
            <TableRow key={car.id} className={car.current_operator_id === user?.id ? 'bg-primary/5' : ''}>
              <TableCell className="font-mono font-semibold">{car.vin}</TableCell>
              <TableCell>{car.model || '-'}</TableCell>
              <TableCell>
                <StatusBadge status={car.status} />
              </TableCell>
              <TableCell>
                {car.current_operator ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 cursor-default">
                          <Avatar className="h-7 w-7">
                            <AvatarFallback className="text-xs">
                              {getInitials(car.current_operator.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className={car.current_operator_id === user?.id ? 'font-semibold text-primary' : ''}>
                            {car.current_operator_id === user?.id ? t('operator.you') : car.current_operator.full_name}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-sm">
                          <div className="font-semibold">{car.current_operator.full_name}</div>
                          {car.claimed_at && (
                            <div className="text-muted-foreground">
                              {t('workflow.claimedAt', { time: formatRelativeTime(car.claimed_at) })}
                            </div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <span className="text-muted-foreground">{t('stations.available')}</span>
                )}
              </TableCell>
              <TableCell>
                {car.last_activity_at ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 text-muted-foreground cursor-default">
                          <Activity className="h-3 w-3" />
                          <span className="text-xs">{formatRelativeTime(car.last_activity_at)}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-xs">{t('operator.lastActivity')}</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <span className="text-muted-foreground text-xs">{t('operator.noActivity')}</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant={getButtonVariant(car)}
                  onClick={() => {
                    if (!stationClaim) {
                      toast.error(t('operator.mustClaimStation'));
                      return;
                    }
                    handleClaimCar(car);
                  }}
                >
                  {car.current_operator_id && car.current_operator_id !== user?.id && (
                    <AlertTriangle className="mr-2 h-4 w-4" />
                  )}
                  {car.current_operator_id !== user?.id && <PlayCircle className="mr-2 h-4 w-4" />}
                  {getButtonLabel(car)}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted">
      <AppHeader
        backTo="/"
        backLabel={t('app.backToDashboard')}
        title={t('dashboard.operatorMode')}
        subtitle={t('dashboard.operatorDesc')}
        rightContent={(
          <SiteSelector
            className="ml-auto"
            selectedSiteId={currentLineId || undefined}
            onSiteChange={setCurrentLineId}
            showWhenSingle
          />
        )}
      />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Station Claim Status */}
          {loadingStation ? (
            <Card className="mb-6">
              <CardContent className="py-6">
                <div className="text-center text-muted-foreground">{t('operator.loadingStation')}</div>
              </CardContent>
            </Card>
          ) : !stationClaim || !stationClaim.station ? (
            <Card className="mb-6 border-orange-500 bg-orange-500/5">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    <div>
                      <div className="font-semibold text-orange-700 dark:text-orange-400">
                        {t('operator.noStationClaimed')}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {t('stations.claimToBegin')}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="default"
                    onClick={() => navigate('/stations')}
                  >
                    <MapPin className="mr-2 h-4 w-4" />
                    {t('operator.selectStation')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-6 border-primary">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-success" />
                    <div>
                      <div className="font-semibold">
                        {t('stations.stationLabel', { number: stationClaim.station.station_number })}
                      </div>
                      {stationClaim.station.name && (
                        <div className="text-sm text-muted-foreground">
                          {stationClaim.station.name}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/stations')}
                  >
                    {t('stations.changeStation')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">{t('operator.identifyVehicle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form id="vin-form" onSubmit={handleVinSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="vin">{t('operator.vinNumber')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="vin"
                      type="text"
                      placeholder={t('operator.vinPlaceholder')}
                      value={vin}
                      onChange={(e) => setVin(e.target.value)}
                      maxLength={17}
                      className="h-touch text-lg font-mono uppercase flex-1"
                      autoFocus
                    />
                    <VinScanner onScanSuccess={handleScanSuccess} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('operator.vinHelper')}
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full h-touch text-lg"
                  disabled={loading}
                >
                  <Search className="mr-2 h-5 w-5" />
                  {loading ? t('operator.processing') : t('operator.startWork')}
                </Button>
              </form>

              <div className="mt-8 p-4 bg-muted rounded-lg">
                <h3 className="font-semibold mb-2">{t('operator.quickTips')}</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {t('operator.tips.documentation')}</li>
                  <li>• {t('operator.tips.location')}</li>
                  <li>• {t('operator.tips.newEntry')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-2xl">{t('operator.vehicleList')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingCars ? (
                <div className="text-center py-8 text-muted-foreground">{t('operator.loadingVehicles')}</div>
              ) : filteredCars.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {vin ? t('operator.noVehiclesMatch') : t('operator.noVehiclesFound')}
                </div>
              ) : (
                <Tabs defaultValue="available" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="available" className="flex items-center gap-2">
                      {t('operator.tabs.available')}
                      <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{availableCars.length}</span>
                    </TabsTrigger>
                    <TabsTrigger value="claimed" className="flex items-center gap-2">
                      {t('operator.tabs.claimed')}
                      <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{claimedCars.length}</span>
                    </TabsTrigger>
                    <TabsTrigger value="all" className="flex items-center gap-2">
                      {t('operator.tabs.all')}
                      <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{filteredCars.length}</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="available" className="mt-4">
                    {renderCarTable(availableCars, t('operator.emptyStates.noAvailable'))}
                  </TabsContent>

                  <TabsContent value="claimed" className="mt-4">
                    {renderCarTable(claimedCars, t('operator.emptyStates.noClaimed'))}
                  </TabsContent>

                  <TabsContent value="all" className="mt-4">
                    {renderCarTable(filteredCars, t('operator.emptyStates.noVehicles'))}
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Force Claim Warning Dialog */}
      <AlertDialog open={!!forceClaimCar} onOpenChange={() => setForceClaimCar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t('operator.takeOverVehicleTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription
              dangerouslySetInnerHTML={{
                __html: t('operator.takeOverVehicleDescription', {
                  name: forceClaimCar?.current_operator?.full_name || t('stations.anotherOperator'),
                  claimedAt: forceClaimCar?.claimed_at
                    ? ` (${t('workflow.claimedAt', { time: formatRelativeTime(forceClaimCar.claimed_at) })})`
                    : '',
                }),
              }}
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('stations.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleForceClaim} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('operator.takeOver')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Operator;
